// Servicio de Clientes (Customers)
// Gestiona información de clientes en Firestore
// Los clientes se identifican por su email (clave única)

import { getCollection, getDocument, updateDocument } from './firestore.service'
import { getAllOrders } from './orders.service'
import { doc, setDoc } from 'firebase/firestore'
import { db } from './firebase.config'

const CUSTOMERS_COLLECTION = 'customers'

/**
 * Obtener todos los clientes
 * Combina datos de la colección customers con datos extraídos de pedidos
 * @returns {Promise<Array>} Array de clientes con estadísticas
 */
export const getAllCustomers = async () => {
  try {
    // Obtener clientes de la colección
    const customersFromCollection = await getCollection(CUSTOMERS_COLLECTION, [], 'createdAt', 'desc')
    
    // Obtener todos los pedidos para calcular estadísticas
    const orders = await getAllOrders()
    
    // Crear un mapa de clientes por email
    const customersMap = new Map()
    
    // Primero, agregar clientes de la colección
    customersFromCollection.forEach(customer => {
      customersMap.set(customer.email, {
        ...customer,
        totalOrders: 0,
        totalSpent: 0,
        firstOrderDate: null,
        lastOrderDate: null,
        orderIds: []
      })
    })
    
    // Luego, procesar pedidos para agregar/actualizar estadísticas
    orders.forEach(order => {
      const email = order.customer?.email
      if (!email) return
      
      if (!customersMap.has(email)) {
        // Crear nuevo cliente desde el pedido
        customersMap.set(email, {
          email: email,
          name: order.customer?.name || 'Sin nombre',
          phones: order.customer?.phone ? [order.customer.phone] : [],
          primaryPhoneIndex: 0,
          addresses: order.customer?.address ? [order.customer.address] : [],
          primaryAddressIndex: 0,
          totalOrders: 0,
          totalSpent: 0,
          firstOrderDate: null,
          lastOrderDate: null,
          orderIds: []
        })
      }
      
      const customer = customersMap.get(email)
      
      // Actualizar estadísticas
      customer.totalOrders += 1
      customer.totalSpent += (order.total || 0)
      customer.orderIds.push(order.id)
      
      // Actualizar fechas
      let orderDate = null
      if (order.createdAt) {
        if (order.createdAt.toDate) {
          orderDate = order.createdAt.toDate()
        } else if (order.createdAt instanceof Date) {
          orderDate = order.createdAt
        } else if (typeof order.createdAt === 'string') {
          orderDate = new Date(order.createdAt)
        } else {
          orderDate = new Date()
        }
      } else {
        orderDate = new Date()
      }
      
      if (!customer.firstOrderDate || orderDate < customer.firstOrderDate) {
        customer.firstOrderDate = orderDate
      }
      
      if (!customer.lastOrderDate || orderDate > customer.lastOrderDate) {
        customer.lastOrderDate = orderDate
      }
      
      // Agregar teléfono si no existe
      const phone = order.customer?.phone
      if (phone && customer.phones && !customer.phones.includes(phone)) {
        if (!customer.phones) customer.phones = []
        customer.phones.push(phone)
      }
      
      // Agregar dirección si no existe
      const address = order.customer?.address
      if (address) {
        const addressString = `${address.street}, ${address.city}, ${address.province}`
        if (!customer.addresses) customer.addresses = []
        
        const addressExists = customer.addresses.some(addr => {
          const existingString = `${addr.street}, ${addr.city}, ${addr.province}`
          return existingString === addressString
        })
        
        if (!addressExists) {
          customer.addresses.push({
            street: address.street,
            city: address.city,
            postalCode: address.postalCode || '',
            province: address.province
          })
        }
      }
      
      // Actualizar nombre si está vacío
      if (!customer.name || customer.name === 'Sin nombre') {
        customer.name = order.customer?.name || customer.name
      }
    })
    
    // Convertir Map a Array y ordenar por último pedido (más reciente primero)
    const customers = Array.from(customersMap.values())
    customers.sort((a, b) => {
      const dateA = a.lastOrderDate || new Date(0)
      const dateB = b.lastOrderDate || new Date(0)
      return dateB - dateA
    })
    
    return customers
  } catch (error) {
    console.error('Error obteniendo clientes:', error)
    throw error
  }
}

/**
 * Obtener un cliente por email
 * @param {string} email - Email del cliente
 * @returns {Promise<Object|null>} Cliente con información detallada o null
 */
export const getCustomerByEmail = async (email) => {
  try {
    // Intentar obtener de la colección
    const customerDoc = await getDocument(CUSTOMERS_COLLECTION, email)
    
    if (customerDoc) {
      // Si existe en la colección, obtener estadísticas de pedidos
      const orders = await getCustomerOrders(email)
      return {
        ...customerDoc,
        totalOrders: orders.length,
        totalSpent: orders.reduce((sum, order) => sum + (order.total || 0), 0),
        firstOrderDate: orders.length > 0 ? orders[orders.length - 1].createdAt : null,
        lastOrderDate: orders.length > 0 ? orders[0].createdAt : null,
        orderIds: orders.map(o => o.id)
      }
    }
    
    // Si no existe, intentar extraer de pedidos
    const customers = await getAllCustomers()
    return customers.find(customer => customer.email === email) || null
  } catch (error) {
    console.error('Error obteniendo cliente por email:', error)
    throw error
  }
}

const customerProfilePromises = new Map()

export const getCustomerProfileByEmail = async (email) => {
  if (!email) return null
  const key = String(email).trim().toLowerCase()
  if (!key) return null
  if (customerProfilePromises.has(key)) return customerProfilePromises.get(key)
  const promise = (async () => {
    try {
      const customerDoc = await getDocument(CUSTOMERS_COLLECTION, key)
      return customerDoc || null
    } catch (error) {
      console.error('Error obteniendo perfil de cliente por email:', error)
      return null
    } finally {
      customerProfilePromises.delete(key)
    }
  })()
  customerProfilePromises.set(key, promise)
  return promise
}

/**
 * Crear o actualizar un cliente
 * @param {Object} customerData - Datos del cliente
 * @returns {Promise<string>} Email del cliente (ID del documento)
 */
export const saveCustomer = async (customerData) => {
  try {
    const { email, name, phones = [], primaryPhoneIndex = 0, addresses = [], primaryAddressIndex = 0, notes = '' } = customerData
    
    // Preparar datos del cliente
    const customerDoc = {
      email,
      name,
      phones: Array.isArray(phones) ? phones : [phones].filter(Boolean),
      primaryPhoneIndex: primaryPhoneIndex >= 0 && primaryPhoneIndex < phones.length ? primaryPhoneIndex : 0,
      addresses: Array.isArray(addresses) ? addresses : [],
      primaryAddressIndex: primaryAddressIndex >= 0 && primaryAddressIndex < addresses.length ? primaryAddressIndex : 0,
      notes: notes || '',
      updatedAt: new Date().toISOString()
    }
    
    // Verificar si el cliente ya existe
    const existing = await getDocument(CUSTOMERS_COLLECTION, email)
    
    if (existing) {
      // Actualizar
      await updateDocument(CUSTOMERS_COLLECTION, email, customerDoc)
    } else {
      // Crear nuevo
      customerDoc.createdAt = new Date().toISOString()
      // Usar email como ID del documento
      await setDoc(doc(db, CUSTOMERS_COLLECTION, email), customerDoc)
    }
    
    return email
  } catch (error) {
    console.error('Error guardando cliente:', error)
    throw error
  }
}

/**
 * Obtener pedidos de un cliente por email
 * @param {string} email - Email del cliente
 * @returns {Promise<Array>} Array de pedidos del cliente
 */
export const getCustomerOrders = async (email) => {
  try {
    const { getOrdersByEmail } = await import('./orders.service')
    return await getOrdersByEmail(email)
  } catch (error) {
    console.error('Error obteniendo pedidos del cliente:', error)
    throw error
  }
}

/**
 * Actualizar datos del cliente desde un pedido
 * Se llama automáticamente cuando se crea un pedido
 * @param {Object} orderCustomerData - Datos del cliente del pedido
 * @returns {Promise<void>}
 */
export const updateCustomerFromOrder = async (orderCustomerData) => {
  try {
    const { email, phone, address } = orderCustomerData
    if (!email) return
    
    // Obtener cliente existente o crear uno nuevo
    let customer = await getDocument(CUSTOMERS_COLLECTION, email)
    
    if (!customer) {
      // Crear nuevo cliente
      customer = {
        email,
        name: orderCustomerData.name || '',
        phones: phone ? [phone] : [],
        primaryPhoneIndex: 0,
        addresses: address ? [address] : [],
        primaryAddressIndex: 0,
        notes: ''
      }
    } else {
      // Actualizar cliente existente
      // Agregar teléfono si no existe
      if (phone && (!customer.phones || !customer.phones.includes(phone))) {
        if (!customer.phones) customer.phones = []
        customer.phones.push(phone)
      }
      
      // Agregar dirección si no existe
      if (address) {
        const addressString = `${address.street}, ${address.city}, ${address.province}`
        if (!customer.addresses) customer.addresses = []
        
        const addressExists = customer.addresses.some(addr => {
          const existingString = `${addr.street}, ${addr.city}, ${addr.province}`
          return existingString === addressString
        })
        
        if (!addressExists) {
          customer.addresses.push(address)
        }
      }
      
      // Actualizar nombre si está vacío
      if (!customer.name && orderCustomerData.name) {
        customer.name = orderCustomerData.name
      }
    }
    
    // Guardar cliente
    await saveCustomer(customer)
  } catch (error) {
    console.error('Error actualizando cliente desde pedido:', error)
    // No lanzar error para no interrumpir la creación del pedido
  }
}

export default {
  getAllCustomers,
  getCustomerByEmail,
  saveCustomer,
  getCustomerOrders,
  updateCustomerFromOrder
}
